import { reactive, watch } from 'vue';
import { classifyStorageError, DexieStorage, FolderStorage, Logger, Metrics, type ModuleAvailability, type StorageAdapter, type StorageFailure } from '@vp/core';
import { DEFAULT_PATH_STYLE, DEFAULT_PEN_STYLE, DEFAULT_SETTINGS, isBrush, isStrokeMode, type EditorSettings, type PathToolStyle, type PenToolStyle } from '@vp/render';

/** Application-wide singletons: storage, diagnostics, settings. */
/**
 * The active profile's database (docs/TZ-PROFILES.md): its app settings, journal, modules — and its
 * canvases unless the profile keeps them in a folder. Which database is decided when the profile is
 * opened (`openProfileDatabase`), before anything reads.
 */
let profileDb = new DexieStorage('vp-guest');
export const browserStorage: DexieStorage = new Proxy({} as DexieStorage, {
  get(_t, key) {
    const v = Reflect.get(profileDb, key, profileDb) as unknown;
    return typeof v === 'function' ? (v as (...a: unknown[]) => unknown).bind(profileDb) : v;
  },
});
/** Use this profile's database from now on (once, when the profile is opened). */
export function openProfileDatabase(name: string): void {
  profileDb = new DexieStorage(name);
}
let active: StorageAdapter = browserStorage;
/**
 * What the whole app reads and writes through: the browser's database, or a folder the user chose
 * (`FolderStorage`, see storage-location.ts). Chosen once at start, before anything reads.
 */
export const storage: StorageAdapter = new Proxy({} as StorageAdapter, {
  get(_t, key) {
    const v = Reflect.get(active, key, active) as unknown;
    return typeof v === 'function' ? (v as (...a: unknown[]) => unknown).bind(active) : v;
  },
});
export function useStorage(a: StorageAdapter): void {
  active = a;
}
/** The folder the canvases are in, null when they are in the browser. */
export function activeFolder(): FolderStorage | null {
  return active instanceof FolderStorage ? active : null;
}
export const logger = new Logger(storage, { console: import.meta.env.DEV });
export const metrics = new Metrics();

export interface AppSettings {
  renderer: 'webgl2' | 'canvas2d';
  devMode: boolean;
  perfPanel: boolean;
  /** Where the diagnostics card sits on the canvas (null: default corner). */
  perfPanelPos: { left: number; top: number } | null;
  treeOpen: boolean;
  minimap: boolean;
  background: string;
  textLodPx: number;
  editor: EditorSettings;
  /** Freehand pencil (B). */
  pen: PenToolStyle;
  /** Bezier pen (P). */
  pathPen: PathToolStyle;
  /** Appearance: theme, accent color of buttons, dark canvas in the dark theme. */
  theme: 'system' | 'light' | 'dark';
  /** How see-through the menu and the canvas panel are: 0 solid, 100 clear glass. */
  glass: number;
  /** Solid panels, no motion: transparency, the name card and the other effects stay still. */
  noMotion: boolean;
  accent: string;
  /** Text / icons on accent buttons and accent-colored text; '' = automatic (readable). */
  onAccent: string;
  accentInk: string;
  darkCanvas: boolean;
  /** Modules whose panel button the user took out of the top-right bar. */
  hiddenPanelButtons: string[];
  /** The user's order of module buttons in the top-right bar (module ids). */
  panelButtonOrder: string[];
  /** Properties of the selection in the right sidebar instead of the top bar. */
  propsDocked: boolean;
  /** Where the user put the properties of a module's objects (module id → docked); wins over the manifest default. */
  propsDockedModules: Record<string, boolean>;
}

/** Preset canvas backgrounds offered in settings. */
export const CANVAS_BACKGROUNDS = ['#f8fafc', '#ffffff', '#f1f5f9', '#fefce8', '#f0fdf4', '#0f172a'];

export const settings = reactive<AppSettings>({
  renderer: 'webgl2',
  devMode: false,
  perfPanel: false,
  perfPanelPos: null,
  treeOpen: false,
  minimap: true,
  background: '#f8fafc',
  textLodPx: 4,
  editor: structuredClone(DEFAULT_SETTINGS),
  pen: { ...DEFAULT_PEN_STYLE },
  pathPen: { ...DEFAULT_PATH_STYLE },
  theme: 'system',
  glass: 82,
  noMotion: false,
  accent: '#5e9cff',
  onAccent: '',
  accentInk: '',
  darkCanvas: true,
  hiddenPanelButtons: [],
  panelButtonOrder: [],
  propsDocked: false,
  propsDockedModules: {},
});

let settingsLoaded = false;

/** The accent before the identity change. Saved copies of it become the new base blue. */
const PREVIOUS_ACCENT = '#2563eb';

function emptyDblClick(v: unknown): EditorSettings['emptyDblClick'] {
  return v === 'text' || v === 'zoom' || v === 'none' ? v : 'text';
}

export async function loadSettings(): Promise<void> {
  const saved = await storage.getSetting<Partial<AppSettings>>('app');
  if (saved) {
    Object.assign(settings, {
      ...saved,
      editor: {
        ...DEFAULT_SETTINGS,
        ...(saved.editor ?? {}),
        grid: { ...DEFAULT_SETTINGS.grid, ...(saved.editor?.grid ?? {}) },
        emptyDblClick: emptyDblClick(saved.editor?.emptyDblClick),
      },
      pen: {
        ...DEFAULT_PEN_STYLE,
        ...(saved.pen ?? {}),
        brush: isBrush(saved.pen?.brush) ? saved.pen.brush : DEFAULT_PEN_STYLE.brush,
        mode: isStrokeMode(saved.pen?.mode) ? saved.pen.mode : DEFAULT_PEN_STYLE.mode,
      },
      pathPen: { ...DEFAULT_PATH_STYLE, ...(saved.pathPen ?? {}) },
    });
    if (settings.accent.toLowerCase() === PREVIOUS_ACCENT) {
      settings.accent = '#5e9cff';
      settings.onAccent = '';
      settings.accentInk = '';
    }
  }
  const forced = new URLSearchParams(location.search).get('renderer');
  if (forced === 'canvas2d' || forced === 'webgl2') settings.renderer = forced;
  settingsLoaded = true;
}

function settingsSnapshot(): AppSettings {
  return JSON.parse(JSON.stringify(settings)) as AppSettings;
}

watch(
  settings,
  (s) => {
    if (settingsLoaded) void storage.setSetting('app', JSON.parse(JSON.stringify(s)));
  },
  { deep: true },
);

/** Write the current settings at once (the Save button on the profile page). */
export function saveSettingsNow(): Promise<void> {
  return storage.setSetting('app', settingsSnapshot());
}

/**
 * Can the data be kept? 'memory': the browser gives no storage, the app runs from memory and
 * nothing survives closing the tab; 'quota' / 'blocked' / 'failed': saving stopped working
 * (the latest changes are only in memory); 'limited': storage works but is small — typical of a
 * private window, where everything is deleted when it closes; 'folder': the folder with the canvases
 * cannot be reached (access withdrawn, folder moved or deleted).
 */
export type StorageHealthState = 'ok' | 'memory' | 'quota' | 'blocked' | 'failed' | 'limited' | 'folder';
export const storageHealth = reactive({ state: 'ok' as StorageHealthState, quota: 0, dismissed: false });

/** Open the database before anything reads it; without persistence fall back to memory (and say so). */
export async function initStorage(): Promise<void> {
  const probe = await browserStorage.probe();
  if (!probe.ok) {
    const fake = await import('fake-indexeddb');
    browserStorage.useMemoryBackend({ indexedDB: fake.indexedDB, IDBKeyRange: fake.IDBKeyRange });
    storageHealth.state = 'memory';
    logger.warn('storage.unavailable', { failure: probe.failure, error: probe.error instanceof Error ? probe.error.name : 'unknown' });
    return;
  }
  try {
    const est = await browserStorage.estimate();
    const persisted = (await navigator.storage?.persisted?.()) === true;
    // a normal profile gets gigabytes; private windows get a small fixed share
    if (est.quota && est.quota < 300 * 1024 * 1024 && !persisted) {
      storageHealth.state = 'limited';
      storageHealth.quota = est.quota;
    }
  } catch {
    /* no estimate: assume normal */
  }
}

/** A save or write failed: remember why, so the banner can tell what to do. */
export function reportStorageError(e: unknown): StorageFailure {
  const kind = classifyStorageError(e);
  const name = (e as { name?: string } | null)?.name ?? '';
  if (activeFolder() && /^(NotAllowedError|NotFoundError|SecurityError)$/.test(name)) {
    storageHealth.state = 'folder';
    storageHealth.dismissed = false;
    return kind;
  }
  if (storageHealth.state !== 'memory') {
    storageHealth.state = kind === 'unavailable' ? 'failed' : kind;
    storageHealth.dismissed = false;
  }
  return kind;
}

/** Saving works again; returns true when it had been failing. */
export function reportStorageOk(): boolean {
  if (storageHealth.state === 'quota' || storageHealth.state === 'blocked' || storageHealth.state === 'failed' || storageHealth.state === 'folder') {
    storageHealth.state = 'ok';
    return true;
  }
  return false;
}

/** Delegates module availability to the runtime once it exists (used by undo/redo, §12.3). */
export const availability: ModuleAvailability & { impl: ModuleAvailability | null } = {
  impl: null,
  isModuleAvailable(id) {
    return this.impl ? this.impl.isModuleAvailable(id) : false;
  },
  typeFormatVersion(type) {
    return this.impl?.typeFormatVersion(type);
  },
};

/** Ask the browser for persistent storage on first launch (§10.6). */
export async function requestPersistence(): Promise<boolean | undefined> {
  if (!navigator.storage?.persist) return undefined;
  if (await navigator.storage.persisted?.()) return true;
  const asked = await storage.getSetting<boolean>('persistAsked');
  if (asked) return navigator.storage.persisted?.();
  await storage.setSetting('persistAsked', true);
  const granted = await navigator.storage.persist();
  logger.info('storage.persist', { granted });
  return granted;
}
